import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Input, Table, Tooltip, Popconfirm } from "antd";
import {
  SearchOutlined,
  DeleteOutlined,
  EditOutlined,
  ArrowLeftOutlined,
  GroupOutlined,
} from "@mui/icons-material";
import {
  useGetAllUsers,
  useDeleteUser,
} from "../../../api/addEmployee/employee";

const AllEmployee = () => {
  const [searchText, setSearchText] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [selectedDepartment, setSelectedDepartment] = useState(null);

  const navigate = useNavigate();
  const { data: users = [], isLoading } = useGetAllUsers();
  const { mutate: deleteUser, isLoading: isDeleting } = useDeleteUser();

  // ✅ Ensure users is always an array
  const employeeList = Array.isArray(users) ? users : Object.values(users);

  // Extract Unique Departments
  const departments = useMemo(() => {
    if (!employeeList || employeeList.length === 0) return [];
    const departmentMap = {};
    employeeList.forEach((user) => {
      const deptName = user.department || "No Department";
      if (!departmentMap[deptName]) {
        departmentMap[deptName] = { name: deptName, count: 0 };
      }
      departmentMap[deptName].count += 1;
    });
    return Object.values(departmentMap);
  }, [employeeList]);

  // Filter Users by Selected Department
  const filteredUsers = useMemo(() => {
    let deptUsers = selectedDepartment
      ? employeeList.filter(
          (user) => (user.department || "No Department") === selectedDepartment
        )
      : employeeList;

    if (!searchText) return deptUsers;

    const searchLower = searchText.toLowerCase();

    return deptUsers.filter((user) => {
      // Convert mobile to string before searching
      const mobileStr = user.mobile ? String(user.mobile) : "";
      
      return (
        user.firstName?.toLowerCase().includes(searchLower) ||
        user.lastName?.toLowerCase().includes(searchLower) ||
        user.email?.toLowerCase().includes(searchLower) ||
        mobileStr.includes(searchText)
      );
    });
  }, [searchText, employeeList, selectedDepartment]);

  // Employee Table Columns
  const employeeColumns = [
    {
      title: "No.",
      render: (_, __, index) => index + 1 + (page - 1) * pageSize,
    },
    {
      title: "Name",
      render: (_, record) => (
        <span className="text-textBlack">
          {record.firstName} {record.lastName}
        </span>
      ),
    },
    { title: "Mobile", dataIndex: "mobile", responsive: ["md"] },
    { title: "E-Mail", dataIndex: "email", responsive: ["lg"] },
    { title: "Password", dataIndex: "plainPassword", responsive: ["xl"] },
    {
      title: "Status",
      dataIndex: "isActive",
      align: "center",
      render: (isActive) => (
        <span
          className={`px-3 py-1 rounded-full text-sm font-medium ${
            isActive
              ? "bg-green-100 text-green-700 border border-green-400"
              : "bg-red-100 text-red-700 border border-red-400"
          }`}
        >
          {isActive ? "Active" : "Inactive"}
        </span>
      ),
    },
    {
      title: "Action",
      width: 200,
      render: (_, row) => (
        <div className="flex gap-4">
          <Tooltip title="Edit">
            <Button
              icon={<EditOutlined />}
              className="!bg-primary !border-primary text-white"
              onClick={() => navigate(`/employee/edit-user/${row._id}`)}
            />
          </Tooltip>
          <Popconfirm
            title="Are you sure you want to delete this user?"
            onConfirm={() => deleteUser(row._id)}
            okText="Yes"
            cancelText="No"
          >
            <Tooltip title="Delete">
              <Button
                icon={<DeleteOutlined />}
                className="!bg-delete !border-delete text-white"
                loading={isDeleting}
              />
            </Tooltip>
          </Popconfirm>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4 p-4 max-h-screen overflow-auto">
      {/* Back Button */}
      {selectedDepartment && (
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => setSelectedDepartment(null)}
          className="mb-4"
        >
          Back to Departments
        </Button>
      )}

      {/* Search Bar */}
      {selectedDepartment && (
        <div className="flex justify-between flex-wrap gap-4 items-center">
          <div className="relative w-80">
            <Input
              placeholder="Search employee..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              prefix={<SearchOutlined />}
            />
          </div>
          <button
            onClick={() => navigate("/employee/add-employee")}
            className="px-6 py-2 rounded text-primary bg-primaryLight hover:bg-primary hover:text-white border border-primary transition-colors"
          >
            Add Employee
          </button>
        </div>
      )}

      {/* Department Cards or Employee Table */}
      {!selectedDepartment ? (
        <div className="space-y-4">
          {/* Add Employee Button on Top */}
          <div className="flex justify-end">
            <button
              onClick={() => navigate("/employee/add-employee")}
              className="px-6 py-2 rounded-lg text-primary bg-white hover:bg-primary hover:text-white border transition-colors"
            >
              + Add Employee
            </button>
          </div>

          {/* Department Cards */}
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {departments.map((dept, index) => (
              <div
                key={index}
                className="bg-white border rounded-2xl shadow hover:shadow-lg p-4 flex flex-col justify-between transition-all"
              >
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <GroupOutlined className="text-primary" />
                    <h2 className="text-lg font-semibold">{dept.name}</h2>
                  </div>
                  <p className="text-gray-600 text-sm">
                    Total Employees:{" "}
                    <span className="font-semibold">{dept.count}</span>
                  </p>
                </div>
                <Button
                  type="primary"
                  className="mt-4 w-full"
                  onClick={() => setSelectedDepartment(dept.name)}
                >
                  View Employees
                </Button>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <Table
          rowKey="_id"
          columns={employeeColumns}
          dataSource={filteredUsers}
          loading={isLoading}
          pagination={{
            current: page,
            pageSize,
            total: filteredUsers.length,
            showSizeChanger: true,
            pageSizeOptions: [5, 10, 20, 40],
            onChange: (current, size) => {
              setPage(current);
              setPageSize(size);
            },
          }}
        />
      )}
    </div>
  );
};

export default AllEmployee;